<template>
  <div class="floating-pane fading">
    <EstimatedDeliveryStatsHeader></EstimatedDeliveryStatsHeader>
    <div class="floating-pane-content">
      <div
        v-if="deal.packageIDs?.length > 0 && deliveryStatistics?.total != null"
        class="audience-info-kpis">
        <EstimatedDeliveryStatsKpiImpressions
          :is-visible="true"
          :label="capitalize($t('messages.dailyImpressionCapacity'))"
          :value="formatNumber(deliveryStatistics?.total.dailyImpressions, '0,0')"
          :is-loading="isLoading"
          :icon="markRaw(IconColoredImpression)" />
        <hr />
        <div class="flex-h">
          <EstimatedDeliveryStatsKpiUnits
            :is-loading="isLoading"
            :unit-count="deliveryStatistics?.total.unitCount"></EstimatedDeliveryStatsKpiUnits>

          <div class="separator" />

          <EstimatedDeliveryStatsKpiSites
            :is-loading="isLoading"
            :site-count="deliveryStatistics?.total.siteCount"></EstimatedDeliveryStatsKpiSites>
        </div>
        <hr />
        <div class="flex-h">
          <EstimatedDeliveryStatsKpiCpm
            :is-loading="isLoading"
            :value="markupCPM"
            :show-tooltip="true"
            :tooltip-content="`eCPM + ${capitalize($t('names.markup'))}`"></EstimatedDeliveryStatsKpiCpm>
          <EstimatedDeliveryStatsKpi
            :is-visible="deal.feeType != null"
            :label="capitalize($t('names.markup'))"
            :value="deal.feeValueFormatted"
            trend-format="+0a"
            :icon="markRaw(IconColoredMarkup)"
            :is-loading="isLoading" />
          <div class="separator" />
        </div>
        <info-message
          class="mx-4 mt-2"
          :message="$t('messages.markupAppliedAtMidnight', { local: localMidnightFormatted })" />
        <EstimatedDeliveryStatsKpi
          v-if="false"
          :is-visible="true"
          :label="capitalize($t('names.margin'))"
          :value="marginFormatted"
          trend-format="+0a"
          :icon="markRaw(IconColoredMargin)"
          :is-loading="isLoading" />
      </div>
      <div
        v-else
        class="px-3 pb-2">
        <div class="alert alert-info icon-sm">
          {{ $t('messages.deliveryStatisticsWillBeUpdatedWhenYouStartSelectingDeals') }}
        </div>
      </div>
    </div>
  </div>

  <div
    v-for="breakdown in breakdowns"
    :key="breakdown.headerLabel"
    class="floating-pane fading">
    <div class="floating-pane-header">
      <component :is="breakdown.headerIcon" />
      <span>{{ breakdown.headerLabel }}</span>
    </div>
    <div
      class="floating-pane-content"
      @wheel.stop>
      <table class="breakdown-table table table-striped">
        <thead>
          <tr>
            <th>{{ capitalize(breakdown.firstColumnHeader) }}</th>
            <th>{{ capitalize($t('names.cpm')) }}</th>
            <th>{{ capitalize($t('messages.dailyImpressions', { n: 2 })) }}</th>
            <th>{{ capitalize($t('names.screen', { n: 2 })) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="[name, stats] in breakdown.data"
            :key="name">
            <td>
              <img
                v-if="breakdown.getLogo(name)"
                v-tooltip="breakdown.getName(name)"
                :src="breakdown.getLogo(name)"
                :alt="breakdown.getName(name)" />
              <span
                v-else
                v-tooltip="breakdown.getName(name)"
                class="ellipsify">
                {{ breakdown.getName(name) }}
              </span>
            </td>
            <td>{{ formatCurrency(stats.eCPM, { currencyCode: deal.currencyInformation.currency }) }}</td>
            <td>{{ formatNumber(stats.dailyImpressions, '0,0.0a').toUpperCase() }}</td>
            <td>{{ formatNumber(stats.unitCount, '0,0a') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, markRaw, Ref, ref, watch } from 'vue';

  import EstimatedDeliveryStatsHeader from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsHeader.vue';
  import EstimatedDeliveryStatsKpi from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpi.vue';
  import EstimatedDeliveryStatsKpiCpm from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiCpm.vue';
  import EstimatedDeliveryStatsKpiImpressions from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiImpressions.vue';
  import EstimatedDeliveryStatsKpiSites from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiSites.vue';
  import EstimatedDeliveryStatsKpiUnits from '@/common/components/estimated-delivery-stats/EstimatedDeliveryStatsKpiUnits.vue';
  import InfoMessage from '@/common/components/generic-messages/InfoMessage.vue';
  import { IconColoredImpression, IconColoredMargin, IconColoredMarkup } from '@/common/icons/colored';
  import { IconDeal, IconMediaOwner } from '@/common/icons/monochrome/';
  import { Package } from '@/common/models';
  import CuratedPackageDeliveryStatistics from '@/common/models/deliveryStatistics/CuratedDeliveryStatistics';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import PackageFeeTypeEnum from '@/common/models/package/PackageFeeType.enum';
  import { DASH, formatCurrency, formatNumber, useInjected } from '@/globals';
  import i18n from '@/vueI18n';

  interface ICuratedDealEstimatedDeliveryStatsProps {
    deliveryStatistics: CuratedPackageDeliveryStatistics;
    mediaOwners: MediaOwner[];
  }

  const { deliveryStatistics, mediaOwners } = defineProps<ICuratedDealEstimatedDeliveryStatsProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');
  const isLoading = ref(false);

  const markupCPM = computed(() => {
    if (deliveryStatistics == null) return DASH;
    let rawMarkup = 0;
    if (deal.value.feeType === PackageFeeTypeEnum.PERCENT) {
      rawMarkup = deliveryStatistics.total.eCPM / (1 - deal.value.feeValue);
    } else if (deal.value.feeType === PackageFeeTypeEnum.FIXED) {
      rawMarkup = deliveryStatistics.total.eCPM + deal.value.feeValue;
    } else {
      rawMarkup = deliveryStatistics.total.eCPM || 0;
    }
    return formatCurrency(rawMarkup, { currencyCode: deal.value.currencyInformation.currency });
  });

  const marginFormatted = computed(() => {
    if (deliveryStatistics == null) return DASH;
    switch (deal.value.feeType) {
      case PackageFeeTypeEnum.PERCENT: {
        const percentageValue =
          (deliveryStatistics.total.dailyImpressions / 1000) * deliveryStatistics.total.eCPM * deal.value.feeValue;
        return formatNumber(percentageValue, percentageValue >= 1000 ? '$0.0a' : '$0.00');
      }
      case PackageFeeTypeEnum.FIXED: {
        const fixed = (deliveryStatistics.total.dailyImpressions / 1000) * deal.value.feeValue;
        return formatNumber(fixed, fixed >= 1000 ? '$0.0a' : '$0.00');
      }
      default:
        return DASH;
    }
  });

  const breakdowns = computed(() => {
    if (deal.value.packageIDs?.length > 0 && deliveryStatistics?.total != null && mediaOwners != null) {
      return [
        {
          headerIcon: markRaw(IconMediaOwner),
          headerLabel: capitalize(i18n.global.t('messages.breakdownByMediaOwner')),
          firstColumnHeader: capitalize(i18n.global.t('names.mediaOwner')),
          getLogo: (name) => mediaOwners?.find((x) => x.id == name)?.logoUrl,
          getName: (name) => mediaOwners?.find((x) => x.id == name)?.name,
          data: Object.entries(deliveryStatistics.bySupplier).sort((a, b) => b[1].unitCount - a[1].unitCount),
        },
        {
          headerIcon: markRaw(IconDeal),
          headerLabel: capitalize(i18n.global.t('messages.breakdownByDeal')),
          firstColumnHeader: capitalize(i18n.global.t('names.deal')),
          getLogo: () => null,
          getName: (name) => `${deal.value.packages.find((x) => x.id == name)?.dealID}`,
          data: Object.entries(deliveryStatistics.byPackage).sort((a, b) => b[1].unitCount - a[1].unitCount),
        },
      ];
    }
    return [];
  });

  const localMidnightFormatted = computed(() => {
    return DateTime.utc().startOf('day').toLocal().toFormat('HH:mm');
  });

  function startLoading() {
    isLoading.value = true;
  }

  function stopLoading() {
    isLoading.value = false;
  }

  watch(
    () => deliveryStatistics,
    () => {
      stopLoading();
    },
  );

  defineExpose({ startLoading });
</script>

<style scoped lang="scss">
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;
  .breakdown-table {
    td {
      font-family: $monospace-font-family;
      font-size: 0.9em;
      &:first-of-type {
        font-family: $font-family;
      }

      img {
        max-height: 2rem;
        max-width: 7rem;
      }
    }

    td,
    th {
      text-align: right;
      &:first-of-type {
        text-align: left;
      }
    }

    tbody {
      display: block;
      width: 100%;
      overflow-y: auto;
      max-height: 20rem;
    }
    thead,
    tbody tr {
      display: table;
      width: 100%;
      table-layout: fixed; /* even columns width , fix width of table too*/
    }
  }
</style>
